import * as amplitude from "@amplitude/analytics-browser";

/**
 * Thin Amplitude wrapper (architecture §5.5). Emits the four-event funnel
 *   interview_main_viewed → interview_begin_clicked
 *      → interview_end_clicked → interview_completed
 * Each carries `{ email, studyId, study_slug, plan_target, platform, mobile_os }`.
 *
 * Platform detection runs once on init and is stuck as a user property so
 * every event implicitly carries it.
 */

let initialised = false;
/** When true, every tracked event carries `is_test: true` so admin-QA runs
 *  can be filtered out of the real funnel. Set once at entry (see
 *  setTestMode); module-level so it survives SPA navigation to /complete. */
let testMode = false;

/** Mark (or unmark) this session as an admin-QA test. Idempotent. */
export function setTestMode(value: boolean): void {
  testMode = value;
}

interface PlatformInfo {
  platform: "web" | "mobile";
  mobile_os: "ios" | "android" | null;
}

function detectPlatform(): PlatformInfo {
  if (typeof navigator === "undefined") {
    return { platform: "web", mobile_os: null };
  }
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isMobile = isIOS || isAndroid || /Mobi/i.test(ua);
  return {
    platform: isMobile ? "mobile" : "web",
    mobile_os: isIOS ? "ios" : isAndroid ? "android" : null,
  };
}

/** Initialise Amplitude. Safe to call multiple times — only the first wins.
 *  No-op when VITE_AMPLITUDE_API_KEY isn't set (dev / preview builds).
 *
 *  `userId` is the participant's research-side UUID (users.id) for real
 *  participants. Pass `undefined` for admin-QA/dev sessions — Amplitude then
 *  identifies by anonymous device_id. NEVER pass the raw email (PII). */
export function initAmplitude(userId?: string): void {
  if (initialised) return;
  const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY as string | undefined;
  if (!apiKey) return;
  // Data residency: the SDK defaults to the US endpoint (api2.amplitude.com).
  // If the Amplitude project lives on EU servers, events sent to US silently
  // vanish — set VITE_AMPLITUDE_SERVER_ZONE=EU to route to api.eu.amplitude.com.
  const serverZone =
    (import.meta.env.VITE_AMPLITUDE_SERVER_ZONE as string | undefined)
      ?.trim()
      .toUpperCase() === "EU"
      ? "EU"
      : "US";
  amplitude.init(apiKey, userId || undefined, {
    autocapture: false,
    serverZone,
  });
  // Sticky user properties — every subsequent event implicitly carries them.
  // v2 SDK uses the Identify API (the v1 `setUserProperties` helper is gone).
  const info = detectPlatform();
  const identify = new amplitude.Identify();
  identify.set("platform", info.platform);
  if (info.mobile_os) identify.set("mobile_os", info.mobile_os);
  amplitude.identify(identify);
  initialised = true;
}

interface BaseProps {
  studyId: string;
  /** Human-readable study label — what researchers filter on. */
  study_slug?: string;
  plan_target?: string | null;
}

function track(name: string, props: BaseProps): void {
  if (!initialised) return;
  amplitude.track(name, { ...props, ...detectPlatform(), is_test: testMode });
}

export const events = {
  /** Page mounted after autologin resolved (architecture §5.5). */
  mainViewed: (p: BaseProps): void => track("interviewMainViewed", p),
  /** Start button clicked. */
  beginClicked: (p: BaseProps): void => track("interviewBeginClicked", p),
  /** Single end-of-call event. `end_reason` tells manual from automatic:
   *  user (pressed End) | agent_ended (Lea wrapped up) | time_up (duration
   *  cap) | connection_lost. (No separate auto-completed event — read the
   *  reason instead.) */
  endClicked: (p: BaseProps & { end_reason?: string }): void =>
    track("interviewEndClicked", p),
  /** Fired on /complete page mount — the conversion event. */
  completed: (p: BaseProps): void => track("interviewCompleted", p),
  /** WebRTC peer / data channel died mid-call — the page auto-ended the
   *  interview to save the transcript-so-far. Watch this to size the
   *  "hangs around minute N" problem in the field. */
  connectionLost: (p: BaseProps): void => track("interviewConnectionLost", p),
};
